import client from './client';

// ── Applications ────────────────────────────────────────────────────────────

export const getApplications = (params) =>
  client.get('/api/admin/applications', { params });

export const getApplication = (id) =>
  client.get(`/api/admin/applications/${id}`);

export const approveApplication = (id, notes) =>
  client.post(`/api/admin/applications/${id}/approve`, { notes });

export const rejectApplication = (id, reason) =>
  client.post(`/api/admin/applications/${id}/reject`, { reason });

export const suspendApplication = (id, reason) =>
  client.post(`/api/admin/applications/${id}/suspend`, { reason });

export const reinstateApplication = (id, notes) =>
  client.post(`/api/admin/applications/${id}/reinstate`, { notes });

export const updatePayoutStatus = (id, payout_status, reason) =>
  client.patch(`/api/admin/applications/${id}/payout-status`, { payout_status, reason });

export const requestChangesApplication = (id, reason) =>
  client.post(`/api/admin/applications/${id}/request-changes`, { reason });

// ── Vehicle moderation (Vehicle Owner Onboarding v2.1) ───────────────────────

export const approveVehicleModeration = (id) =>
  client.post(`/api/admin/vehicles/${id}/approve`);

export const rejectVehicleModeration = (id, reason) =>
  client.post(`/api/admin/vehicles/${id}/reject`, { reason });

export const requestChangesVehicleModeration = (id, reason) =>
  client.post(`/api/admin/vehicles/${id}/request-changes`, { reason });

// ── Vehicle Owner rule engine (country + account_type required fields) ──────

export const getVehicleOwnerRequirements = () =>
  client.get('/api/admin/vehicle-owner-requirements');

export const setVehicleOwnerRequirement = (country_code, account_type, region, requirement_key, is_required) =>
  client.put('/api/admin/vehicle-owner-requirements', { country_code, account_type, region, requirement_key, is_required });

export const getVehicleOwnerRequirementsAuditLog = () =>
  client.get('/api/admin/vehicle-owner-requirements/audit-log');

// ── Vehicle category rules (per-country allow-list for the "Cars Only" gate) ─

export const getVehicleCategoryConfig = () =>
  client.get('/api/admin/vehicle-category-config');

export const setVehicleCategoryAllowed = (country_code, category_key, is_allowed) =>
  client.put('/api/admin/vehicle-category-config', { country_code, category_key, is_allowed });

export const deleteApplications = (ids) =>
  client.delete('/api/admin/applications', { data: { ids } });

export const deleteAllApplications = (status) =>
  client.delete('/api/admin/applications', { data: { all: true, status } });

// ── Users ────────────────────────────────────────────────────────────────────

export const getUsers = (params) =>
  client.get('/api/admin/users', { params });

export const getUser = (id) =>
  client.get(`/api/admin/users/${id}`);

export const blockUser = (id, reason) =>
  client.post(`/api/admin/users/${id}/block`, { reason });

export const unblockUser = (id) =>
  client.post(`/api/admin/users/${id}/unblock`);

// Edit an account (name, contact, address, country, date of birth, host
// type) -- logged with old -> new values and the reason.
export const updateUser = (id, data) =>
  client.patch(`/api/admin/users/${id}`, data);

export const getUserHistory = (id) =>
  client.get(`/api/admin/users/${id}/history`);

// `confirm`: the six-digit customer number for one account, or the number
// of accounts for several. Checked again on the server.
export const deleteUsers = (ids, confirm) =>
  client.delete('/api/admin/users', { data: { ids, confirm } });

// ── Verifications ─────────────────────────────────────────────────────────────

export const getVerifications = (params) =>
  client.get('/api/admin/verifications', { params });

export const approveVerification = (id, notes) =>
  client.post(`/api/admin/verifications/${id}/approve`, { notes });

export const rejectVerification = (id, reason) =>
  client.post(`/api/admin/verifications/${id}/reject`, { reason });

// ── Logs ─────────────────────────────────────────────────────────────────────

export const getLogs = (params) =>
  client.get('/api/admin/logs', { params });

// ── Permissions / Staff ──────────────────────────────────────────────────────

export const getPermissionsInfo = () =>
  client.get('/api/admin/permissions-info');

export const getStaff = (params) =>
  client.get('/api/admin/staff', { params });

export const getStaffMember = (id) =>
  client.get(`/api/admin/staff/${id}`);

export const createStaff = (data) =>
  client.post('/api/admin/staff', data);

export const inviteStaff = (data) =>
  client.post('/api/admin/staff/invite', data);

export const updateStaff = (id, data) =>
  client.patch(`/api/admin/staff/${id}`, data);

export const deactivateStaff = (id) =>
  client.post(`/api/admin/staff/${id}/deactivate`);

export const activateStaff = (id) =>
  client.post(`/api/admin/staff/${id}/activate`);

// ── Vehicles (MS 45 moderation) ────────────────────────────────────────────

export const getVehicles = (params) =>
  client.get('/api/admin/vehicles', { params });

export const updateVehicle = (id, data) =>
  client.patch(`/api/admin/vehicles/${id}`, data);

export const deleteVehicle = (id) =>
  client.delete(`/api/admin/vehicles/${id}`);

export const getVehicleTripLocation = (id) =>
  client.get(`/api/admin/vehicles/${id}/trip-location`);

export const getDirectorCount = () =>
  client.get('/api/admin/staff/director-count');

export const bootstrapDirector = () =>
  client.post('/api/admin/staff/bootstrap-director');

export const getAdminProtocols = (params) => client.get('/api/admin/protocols', { params });
export const getAdminProtocolDetail = (id) => client.get(`/api/admin/protocols/${id}`);

export const getFleetApprovals = () => client.get('/api/admin/fleet-approval/pending');
export const approveFleet = (id) => client.post(`/api/admin/fleet-approval/${id}/approve`);
export const rejectFleet = (id, reason) => client.post(`/api/admin/fleet-approval/${id}/reject`, { reason });
export const getAdminProtocolLocations = (bookingId) => client.get(`/api/admin/bookings/${bookingId}/locations`);

// ── Damage Cases ────────────────────────────────────────────────────────────

export const getDamageCases = () => client.get('/api/damage-cases/admin/all');
export const getDamageCase = (id) => client.get(`/api/damage-cases/admin/${id}`);
export const addCaseNote = (id, description) => client.post(`/api/damage-cases/admin/${id}/note`, { description });
export const decideCase = (id, body) => client.post(`/api/damage-cases/admin/${id}/decide`, body);

// ── Carsharing Damage Reports ─────────────────────────────────────────────────

export const getCarshareDamageReports = () => client.get('/api/carshare/admin/damage-reports');
export const getAllDamageReports = () => client.get('/api/admin/damage-reports');


export const getCarshareRegistrations = () => client.get('/api/carshare/registrations');
export const getPendingCarshareRegs = () => client.get('/api/carshare/admin/pending');
export const reviewCarshareReg = (id, body) => client.post(`/api/carshare/admin/${id}/review`, body);

// ── Safety Center — incident reports & emergency numbers ─────────────────────

export const getIncidentReports = (params) =>
  client.get('/api/admin/incident-reports', { params });

export const updateIncidentReportStatus = (id, status) =>
  client.patch(`/api/admin/incident-reports/${id}`, { status });

export const getEmergencyNumbers = () =>
  client.get('/api/admin/emergency-numbers');

export const saveEmergencyNumber = (data) =>
  client.post('/api/admin/emergency-numbers', data);

export const deleteEmergencyNumber = (countryCode) =>
  client.delete(`/api/admin/emergency-numbers/${countryCode}`);

// ── KE provider applications (Kenya Registration Plan) ───────────────────────

export const getProviderApplications = (params) =>
  client.get('/api/admin/provider-applications', { params });

// One application with its documents (short-lived view links).
export const getProviderApplication = (id) =>
  client.get(`/api/admin/provider-applications/${id}`);

export const approveProviderApplication = (id) =>
  client.post(`/api/admin/provider-applications/${id}/approve`);

export const rejectProviderApplication = (id, reason) =>
  client.post(`/api/admin/provider-applications/${id}/reject`, { reason });

// ── Country-Specific Home Pages: per-country category tile toggles ──────────

export const getCategoryConfig = () =>
  client.get('/api/admin/category-config');

export const setCategoryVisibility = (country_code, category_key, is_visible) =>
  client.put('/api/admin/category-config', { country_code, category_key, is_visible });

// ── Registration & Multi-Role Concept: per-country payment method toggles ───

export const getPaymentMethodConfig = () =>
  client.get('/api/admin/payment-method-config');

export const setPaymentMethodEnabled = (country_code, method_key, is_enabled) =>
  client.put('/api/admin/payment-method-config', { country_code, method_key, is_enabled });

// ── Rooms & Marketplace admin (Listing Moderation, Host Verification,
// Booking Oversight, Audit Log, Configuration) ──────────────────────────────

export const getRoomListings = (params) =>
  client.get('/api/rooms/admin/listings', { params });

export const setRoomListingStatus = (id, status, rejection_reason) =>
  client.patch(`/api/rooms/admin/listings/${id}`, { status, rejection_reason });

export const getRoomHosts = (params) =>
  client.get('/api/rooms/admin/hosts', { params });

export const setRoomHostStatus = (id, kyc_status, blocked_reason) =>
  client.patch(`/api/rooms/admin/hosts/${id}`, { kyc_status, blocked_reason });

export const getRoomBookings = (params) =>
  client.get('/api/rooms/admin/bookings', { params });

export const cancelRoomBookingAsAdmin = (id, refund_amount_cents, admin_note) =>
  client.patch(`/api/rooms/admin/bookings/${id}/cancel`, { refund_amount_cents, admin_note });

export const getRoomAuditLog = (params) =>
  client.get('/api/rooms/admin/audit-log', { params });

export const getRoomConfig = () =>
  client.get('/api/rooms/config');

export const setRoomConfigValue = (key, value) =>
  client.patch(`/api/rooms/admin/config/${key}`, { value });

// ── Accommodation/Providers module (Country Control & Self-Service brief) ───

export const getAccommodationOverview = (params) =>
  client.get('/api/rooms/admin/listings/overview', { params });

export const getAccommodationListings = (params) =>
  client.get('/api/rooms/admin/listings', { params });

export const setAccommodationListingStatus = (id, status, rejection_reason) =>
  client.patch(`/api/rooms/admin/listings/${id}`, { status, rejection_reason });

export const getListingCategories = () =>
  client.get('/api/rooms/admin/categories');

export const createListingCategory = (data) =>
  client.post('/api/rooms/admin/categories', data);

export const updateListingCategory = (id, data) =>
  client.patch(`/api/rooms/admin/categories/${id}`, data);

export const getAccommodationBookings = (params) =>
  client.get('/api/rooms/admin/bookings', { params });

// ── Country master data + admin country access ──────────────────────────────

export const getCountries = () =>
  client.get('/api/admin/countries');

export const createCountry = (data) =>
  client.post('/api/admin/countries', data);

export const updateCountry = (code, data) =>
  client.patch(`/api/admin/countries/${code}`, data);

export const getStaffCountries = (id) =>
  client.get(`/api/admin/staff/${id}/countries`);

export const setStaffCountries = (id, country_codes) =>
  client.put(`/api/admin/staff/${id}/countries`, { country_codes });

// ── Data Quality / "Without Country" review ──────────────────────────────────

export const getCountrylessRecords = (params) =>
  client.get('/api/admin/countryless-records', { params });

export const scanCountrylessRecords = () =>
  client.post('/api/admin/countryless-records/scan');

export const assignCountrylessRecord = (id, data) =>
  client.patch(`/api/admin/countryless-records/${id}`, data);

// ── Case Control & Country Permissions ───────────────────────────────────────

export const getCaseQueue = (params) =>
  client.get('/api/admin/cases', { params });

// Permissions & History Development Plan (13.10.2026) — the restricted
// account's own scoped views.
export const getCurrentAssignments = (params) =>
  client.get('/api/admin/cases/current-assignments', { params });

export const getCaseHistory = (params) =>
  client.get('/api/admin/cases/history', { params });

export const getAssignableStaff = (caseType, caseId) =>
  client.get(`/api/admin/cases/${caseType}/${caseId}/assignable-staff`);

export const assignCase = (caseType, caseId, staff_admin_id, message, contractorTerms) =>
  client.post(`/api/admin/cases/${caseType}/${caseId}/assign`, { staff_admin_id, message, ...contractorTerms });

export const bulkAssignCases = (cases, staff_admin_id) =>
  client.post('/api/admin/cases/bulk-assign', { cases, staff_admin_id });

// Case Control › Settings › History & Document Rights (Phase 4 settings page).
export const getDocumentRules = () =>
  client.get('/api/admin/cases/document-rules');

export const getDocumentRuleStaff = () =>
  client.get('/api/admin/cases/document-rules/staff');

export const putProfileDocumentRule = (profile, data) =>
  client.put(`/api/admin/cases/document-rules/profile/${profile}`, data);

export const putPersonDocumentRule = (adminId, data) =>
  client.put(`/api/admin/cases/document-rules/person/${adminId}`, data);

export const resetPersonDocumentRule = (adminId) =>
  client.delete(`/api/admin/cases/document-rules/person/${adminId}`);

// My Account — personal language preference.
export const setMyLanguage = (language) =>
  client.patch('/api/admin/me/language', { language });

// Release access to another person for one case (case_access_grants) —
// the write side of "other people... only through an explicit release".
export const getAccessGrants = (caseType, caseId) =>
  client.get(`/api/admin/cases/${caseType}/${caseId}/access-grants`);

export const releaseAccess = (caseType, caseId, data) =>
  client.post(`/api/admin/cases/${caseType}/${caseId}/release-access`, data);

export const escalateCase = (caseType, caseId, reason) =>
  client.post(`/api/admin/cases/${caseType}/${caseId}/escalate`, { reason });

export const setCasePriority = (caseType, caseId, priority) =>
  client.patch(`/api/admin/cases/${caseType}/${caseId}/priority`, { priority });

export const setCaseDeadline = (caseType, caseId, admin_deadline_at) =>
  client.patch(`/api/admin/cases/${caseType}/${caseId}/deadline`, { admin_deadline_at });

export const takeOverCase = (caseType, caseId) =>
  client.post(`/api/admin/cases/${caseType}/${caseId}/take-over`);

export const setCaseCountry = (caseType, caseId, country_code, reason) =>
  client.patch(`/api/admin/cases/${caseType}/${caseId}/country`, { country_code, reason });

export const getEmergencyAccessGrants = () =>
  client.get('/api/admin/cases/emergency-access');

export const grantEmergencyAccess = (data) =>
  client.post('/api/admin/cases/emergency-access', data);

export const revokeEmergencyAccess = (id) =>
  client.patch(`/api/admin/cases/emergency-access/${id}/revoke`);

export const sendEmergencyTask = (id) =>
  client.post(`/api/admin/cases/emergency-access/${id}/send-task`);

// ── Assignment Workflow (accept / decline / withdraw / return) ──────────────

export const getMyTasks = () =>
  client.get('/api/admin/cases/my-tasks');

export const getCaseAssignmentHistory = (caseType, caseId) =>
  client.get(`/api/admin/cases/${caseType}/${caseId}/assignments`);

export const acceptTask = (assignmentId) =>
  client.post(`/api/admin/cases/assignments/${assignmentId}/accept`);

export const declineTask = (assignmentId, reason, note) =>
  client.post(`/api/admin/cases/assignments/${assignmentId}/decline`, { reason, note });

export const withdrawTask = (assignmentId, reason) =>
  client.post(`/api/admin/cases/assignments/${assignmentId}/withdraw`, { reason });

export const requestTaskReturn = (assignmentId, reason, note) =>
  client.post(`/api/admin/cases/assignments/${assignmentId}/return-request`, { reason, note });

export const confirmTaskReturn = (assignmentId, confirm) =>
  client.post(`/api/admin/cases/assignments/${assignmentId}/return-confirm`, { confirm });

export const redistributeTask = (assignmentId, staff_admin_id) =>
  client.post(`/api/admin/cases/assignments/${assignmentId}/redistribute`, { staff_admin_id });

export const getCaseControlSettings = () =>
  client.get('/api/admin/cases/settings');

export const updateCaseControlSetting = (key, value) =>
  client.patch(`/api/admin/cases/settings/${key}`, { value });

export const getCaseComments = (caseType, caseId) =>
  client.get(`/api/admin/cases/${caseType}/${caseId}/comments`);

export const addCaseComment = (caseType, caseId, content) =>
  client.post(`/api/admin/cases/${caseType}/${caseId}/comments`, { content });

export const requestDeadlineExtension = (assignmentId, requested_minutes, reason) =>
  client.post(`/api/admin/cases/assignments/${assignmentId}/deadline-extension`, { requested_minutes, reason });

export const getDeadlineExtensions = () =>
  client.get('/api/admin/cases/deadline-extensions');

export const decideDeadlineExtension = (extensionId, approve) =>
  client.patch(`/api/admin/cases/deadline-extensions/${extensionId}`, { approve });

// PDF documentation spec: "Submit completed work" (staff) and the Owner's
// "Accepted" / "Rework required" decision (which generates the Delivery
// Note) replace the earlier single-step "mark done".
export const submitWork = (assignmentId, report) =>
  client.post(`/api/admin/cases/assignments/${assignmentId}/submit`, { report });

export const recordDecision = (assignmentId, decision, remarks) =>
  client.post(`/api/admin/cases/assignments/${assignmentId}/decision`, { decision, remarks });

export const getDocumentDownload = (documentId) =>
  client.get(`/api/admin/cases/documents/${documentId}/download`);

export const getLegalEntities = () =>
  client.get('/api/admin/cases/legal-entities');

export const updateLegalEntity = (countryCode, entity_name, address) =>
  client.patch(`/api/admin/cases/legal-entities/${countryCode}`, { entity_name, address });

// ── Activity Log & Reference Numbers ─────────────────────────────────────────

export const getActivityLog = (params) =>
  client.get('/api/admin/activity-log', { params });

export const getActivityLogFilters = () =>
  client.get('/api/admin/activity-log/filters');

// ── Self-claim (personal claim PIN) ───────────────────────────────────────────

export const getClaimableCases = () =>
  client.get('/api/admin/self-claim/claimable');

export const setClaimPin = (pin) =>
  client.post('/api/admin/self-claim/pin', { pin });

export const selfClaimCase = (caseType, caseId, pin) =>
  client.post(`/api/admin/self-claim/${caseType}/${caseId}`, { pin });

// ── Accommodation taxes & local charges (Kayd, 20.10.2026) ──────────────────

export const getRoomTaxRules = (params) =>
  client.get('/api/rooms/admin/tax-rules', { params });

export const createRoomTaxRule = (data) =>
  client.post('/api/rooms/admin/tax-rules', data);

export const updateRoomTaxRule = (id, data) =>
  client.patch(`/api/rooms/admin/tax-rules/${id}`, data);

export const deleteRoomTaxRule = (id) =>
  client.delete(`/api/rooms/admin/tax-rules/${id}`);

// Ride fees, Boda-Boda & Auto (Kayd, 28.10.2026).
export const getRideFeeRules = (params) => client.get('/api/admin/ride-fee-rules', { params });
export const createRideFeeRule = (data) => client.post('/api/admin/ride-fee-rules', data);
export const previewRideFee = (params) => client.get('/api/admin/ride-fee-rules/preview', { params });
export const getRideCashExceptions = () => client.get('/api/admin/ride-fee-rules/cash-exceptions');
export const resolveRideCashException = (rideId, data) => client.post(`/api/admin/ride-fee-rules/cash-exceptions/${rideId}`, data);
export const getAdminPayouts = (params) => client.get('/api/admin/ride-fee-rules/payouts', { params });
export const updateAdminPayoutStatus = (id, status) => client.patch(`/api/admin/ride-fee-rules/payouts/${id}/status`, { status });
export const getAdminClaims = (params) => client.get('/api/admin/ride-fee-rules/claims', { params });
export const updateAdminClaimStatus = (id, status) => client.patch(`/api/admin/ride-fee-rules/claims/${id}/status`, { status });
export const getRideEvents = (rideId) => client.get(`/api/admin/ride-fee-rules/ride-events/${rideId}`);
export const getRideOperations = (params) => client.get('/api/admin/ride-fee-rules/operations', { params });
export const getRideOperation = (rideId) => client.get(`/api/admin/ride-fee-rules/operations/${rideId}`);
export const createRideAdjustment = (rideId, data) => client.post(`/api/admin/ride-fee-rules/operations/${rideId}/adjustments`, data);
export const reverseRideAdjustment = (rideId, adjustmentId, data) => client.post(`/api/admin/ride-fee-rules/operations/${rideId}/adjustments/${adjustmentId}/reverse`, data);
